/**
 * ImportReviewBrowser — one verdict-aware, browsable list for an import
 * review (GDN-2178 / ADR-0076).
 *
 * Replaces the summary-screen + separate-conflict-queue split: every staged
 * resource is a row carrying its classify verdict. Undecided conflicts and
 * ambiguous matches sort to the top with inline resolve controls, so decisions
 * are unmissable without walling them off from the rest. Any row expands to
 * inspect its converted data (the operator's convert-confidence check).
 * Filters slice by type / verdict / search.
 *
 * Commit is progressive-trust (GDN-2250/2251): a decided patient row commits
 * individually, and the current verdict filter commits as a cohort — the host
 * supplies the complete per-verdict patient key sets, since the browser only
 * sees the loaded window.
 *
 * Presentational and self-contained: the host feeds rows + counts and handles
 * the decision/commit callbacks. The host sends the collected decisions as
 * the commit's conflict_resolutions and gates whole-session commit until every
 * conflict is decided.
 */
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { format } from 'date-fns';

import type {
  ImportVerdict,
  ReviewDecision,
  ReviewFieldDiff,
  ReviewMatchCandidate,
  ReviewMatchResolution,
  VerdictCounts,
} from './import-review-types';

// ---------------------------------------------------------------------------
// Row contract
// ---------------------------------------------------------------------------

export interface ReviewResourceRow {
  id: string;
  resourceType: string;
  /** Operator-facing label (PHI allowed in UI, never in telemetry). */
  label: string;
  /** Optional secondary line, e.g. a date or source key. */
  sublabel?: string;
  verdict: ImportVerdict;
  /** Short description of the existing record a match was made against. */
  matchedSummary?: string;
  /** Competing fields (verdict === 'conflict') — the decision. */
  competing?: ReviewFieldDiff[];
  /** Non-competing fills that merge automatically (update / conflict FYI). */
  autoMerge?: ReviewFieldDiff[];
  /** Existing-patient candidates (verdict === 'ambiguous') — the choice. */
  matchCandidates?: ReviewMatchCandidate[];
  /**
   * Why an ambiguous row needs a decision: 'identifier_collision' |
   * 'weak_demographic_match' | 'ambiguous_identifier' | 'ambiguous_match'.
   */
  matchBasis?: string;
  /** Converted payload, shown on inspect — the convert-confidence check. */
  data?: Record<string, unknown>;
  /**
   * Row synthesized by the host from the conflict/match feeds because its
   * staged row is outside the current server page. The server never saw
   * these, so in server-browse mode the type/search filters apply to them
   * client-side (a 120k-row import pins 500+ of them — leaving them exempt
   * buried the filtered results; GDN-2247).
   */
  pinned?: boolean;
  /**
   * The owning patient's source key — set on patient rows so a decided row
   * can commit individually (GDN-2251).
   */
  patientKey?: string;
  /**
   * Owning patient's display name for a child row, resolved server-side from
   * the row's refs (GDN-2264), so it shows regardless of the browse page.
   * Lets the operator see whose chart a staged appointment/med/etc. lands on.
   */
  patientLabel?: string;
  /**
   * A child that references no patient but carries a converter-declared name
   * (the title-match didn't resolve): it will NOT link on commit. Rendered as
   * a warning so the operator can act — link manually or re-match later.
   */
  patientUnlinked?: boolean;
}

export interface ImportReviewBrowserProps {
  sourceLabel?: string;
  totalRecords: number;
  totals: VerdictCounts;
  rows: ReviewResourceRow[];
  /** Controlled decisions by row id (uncontrolled if omitted). */
  decisions?: Record<string, ReviewDecision>;
  onDecisionChange?: (id: string, decision: ReviewDecision | null) => void;
  /** Controlled ambiguous-match resolutions by row id (uncontrolled if omitted). */
  matchResolutions?: Record<string, ReviewMatchResolution>;
  onMatchResolutionChange?: (
    id: string,
    resolution: ReviewMatchResolution | null,
  ) => void;
  /**
   * Progressive-trust commit (GDN-2250/2251). When provided, a decided
   * patient row gets its own Commit button, and the current verdict filter
   * commits as a cohort using the host-supplied complete key sets (the
   * browser only sees the loaded window). The host owns dispatch, gating
   * errors, and refresh.
   */
  commit?: {
    onCommitPatients: (patientKeys: string[]) => void;
    inFlight: boolean;
    /** Complete per-verdict patient key sets across the whole import. */
    patientKeysByVerdict: Partial<Record<ImportVerdict, string[]>>;
  };
  /**
   * Open the source-document preview for a row (GDN-2214). Offered on
   * document_reference rows; the host owns the dialog + fetch.
   */
  onViewDocument?: (rowId: string) => void;
  /**
   * Server-driven browse (GDN-2244). When provided, the type filter, search
   * box, and pagination are controlled by the host, which fetches the matching
   * page from the server — filters cover the whole import, not a loaded
   * window. Verdict chips still slice client-side (conflict/ambiguous rows are
   * always fully loaded and merged into `rows`).
   */
  serverBrowse?: {
    typeFilter: string;
    onTypeFilterChange: (type: string) => void;
    search: string;
    onSearchChange: (search: string) => void;
    page: number;
    onPageChange: (page: number) => void;
    perPage: number;
    totalRows: number;
    /** All resource types in the import (page rows won't contain them all). */
    resourceTypes: string[];
  };
  onCommit?: () => void;
  /**
   * Show the built-in commit bar. Off when a host owns the commit step (e.g.
   * StagedResourcesSection, which has its own gated Commit button).
   */
  showCommitBar?: boolean;
  /** Rendered inside the collapsible Provenance disclosure. */
  provenance?: ReactNode;
}

// ---------------------------------------------------------------------------
// Verdict metadata — badge label, palette, icon. Single source for the chips.
// ---------------------------------------------------------------------------

type VerdictColor = 'info' | 'success' | 'primary' | 'warning' | 'error';

const VERDICT_META: Record<
  ImportVerdict,
  { label: string; color: VerdictColor; icon: ReactElement }
> = {
  new: {
    label: 'New',
    color: 'info',
    icon: <AddCircleOutlinedIcon fontSize="small" />,
  },
  unchanged: {
    label: 'Unchanged',
    color: 'success',
    icon: <CheckCircleOutlinedIcon fontSize="small" />,
  },
  update: {
    label: 'Update',
    color: 'primary',
    icon: <AutoAwesomeOutlinedIcon fontSize="small" />,
  },
  conflict: {
    label: 'Needs review',
    color: 'warning',
    icon: <WarningAmberOutlinedIcon fontSize="small" />,
  },
  ambiguous: {
    label: 'Needs match',
    color: 'warning',
    icon: <PersonSearchOutlinedIcon fontSize="small" />,
  },
  unprocessable: {
    label: "Couldn't read",
    color: 'error',
    icon: <ErrorOutlinedIcon fontSize="small" />,
  },
};

/** Plain-English explanation of each verdict, shown when a row is expanded. */
const VERDICT_BLURB: Record<ImportVerdict, string> = {
  new: 'Not on file — this will be created as a new record.',
  unchanged: 'Already on file and identical — nothing to do.',
  update:
    'Already on file. The import fills fields that were blank; nothing already on the record is changed.',
  conflict:
    'Already on file, but the import has a different value for a field that is already set — choose which to keep.',
  ambiguous:
    'More than one existing patient could be this record — pick the right one, or create a new patient.',
  unprocessable: "Couldn't be read from the source, so it's excluded from the import.",
};

/** Operator-facing explanation of WHY a row is ambiguous (GDN-2244). */
const MATCH_BASIS_BLURB: Record<string, string> = {
  identifier_collision:
    "The import's MRN matches an existing patient, but the name and date of " +
    'birth do not — most likely two different people whose systems reuse the ' +
    'same MRN. Link only if you are sure they are the same person.',
  weak_demographic_match:
    'A similar existing patient was found, but the details do not fully ' +
    'agree — confirm before linking.',
  ambiguous_identifier:
    'More than one existing patient carries this MRN — pick the right one.',
  ambiguous_match:
    'More than one existing patient looks like this record — pick the right one.',
};

const VERDICT_ORDER: ImportVerdict[] = [
  'conflict',
  'ambiguous',
  'update',
  'new',
  'unchanged',
  'unprocessable',
];

type VerdictFilter = ImportVerdict | 'all';

function isConflict(row: ReviewResourceRow): boolean {
  return row.verdict === 'conflict';
}

function isAmbiguous(row: ReviewResourceRow): boolean {
  return row.verdict === 'ambiguous';
}

const ROW_GAP = 1;

// Auto-expand every needs-decision row only up to this many; past it the open
// diffs bury the list (org 3 arrived with 535).
const AUTO_EXPAND_MAX = 25;

// ---------------------------------------------------------------------------
// Field diff — the existing → incoming pair, shared by competing + auto-merge.
// ---------------------------------------------------------------------------

/**
 * Structured values (a patient's primary address arrives as a JSON object)
 * read as raw JSON in diffs. Flatten one level to "street, city, state" so
 * the operator compares values, not syntax. Non-JSON strings pass through.
 */
export function formatFieldValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    const flatten = (v: unknown): string => {
      if (v === null || v === undefined || v === '') return '';
      if (Array.isArray(v)) return v.map(flatten).filter(Boolean).join('; ');
      if (typeof v === 'object')
        return Object.values(v as Record<string, unknown>)
          .map(flatten)
          .filter(Boolean)
          .join(', ');
      return String(v);
    };
    return flatten(parsed) || value;
  } catch {
    return value;
  }
}

/**
 * One-line descriptor for an appointment row: "{type} · {when} · {duration}",
 * e.g. "Psychiatric Intake · Jun 24, 2026 2:00 PM · 60 min". An appointment's
 * raw source key (appointment:<uuid>) tells the operator nothing; the patient
 * leads the row (GDN-2269) and this carries the appointment's own detail.
 * Missing/unparseable parts are dropped rather than shown blank.
 */
export function appointmentDescriptor(
  data: Record<string, unknown> | undefined,
): string {
  const d = data ?? {};
  const str = (k: string): string =>
    typeof d[k] === 'string' ? (d[k] as string) : '';
  const parts: string[] = [];
  const type = (str('encounter_type') || str('title')).replaceAll('_', ' ').trim();
  if (type) parts.push(type);
  const raw = str('start_time');
  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      parts.push(format(parsed, 'MMM d, yyyy h:mm a'));
    }
  }
  const duration = d.duration;
  if (typeof duration === 'number' && duration > 0) parts.push(`${duration} min`);
  return parts.join(' · ');
}

function FieldDiffRow({
  diff,
  mode,
}: {
  diff: ReviewFieldDiff;
  mode: 'competing' | 'fill';
}) {
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}
    >
      <Typography
        variant="caption"
        sx={{ minWidth: 120, color: 'text.secondary' }}
      >
        {diff.field}
      </Typography>
      {mode === 'competing' ? (
        <>
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: 'text.disabled' }}
          >
            {formatFieldValue(diff.existing) || '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            →
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatFieldValue(diff.incoming) || '—'}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatFieldValue(diff.incoming) || '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>
            (field was blank)
          </Typography>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Inspect — flat key/value view of the converted payload.
// ---------------------------------------------------------------------------

/**
 * Render a nested object/array from the converted payload as a readable
 * "key: value · key: value" line instead of raw JSON — a patient's primary
 * address, an appointment's metadata. Field labels are kept (underscores
 * spaced) so this stays a faithful convert-confidence check; empty members are
 * dropped and primitives pass through unchanged.
 */
export function humanizeStructured(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v))
    return v.map(humanizeStructured).filter(Boolean).join('; ');
  if (typeof v === 'object')
    // Filter on the humanized value, not the raw one: a member that is itself
    // an empty object/array (or all-null) humanizes to '' and must drop its
    // label too, else it leaves a dangling "key: " — same as the array branch.
    return Object.entries(v as Record<string, unknown>)
      .map(([k, val]) => [k.replace(/_/g, ' '), humanizeStructured(val)] as const)
      .filter(([, val]) => val !== '')
      .map(([k, val]) => `${k}: ${val}`)
      .join(' · ');
  return String(v);
}

function InspectData({ data }: { data: Record<string, unknown> }) {
  // Keep falsy-but-meaningful values (0, false, ''); only drop null/undefined.
  // This is the PHI-inspection view — a hidden 0-vs-null can matter clinically.
  const entries = Object.entries(data).filter(([, v]) => v != null);
  if (entries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No fields on this record.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 2,
        rowGap: 0.5,
      }}
    >
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography variant="caption" color="text.secondary">
            {k}
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
          >
            {/* Nested objects (address, metadata) read as "key: value", not
                raw JSON; a value that flattens to nothing falls back to JSON. */}
            {typeof v === 'object'
              ? humanizeStructured(v) || JSON.stringify(v)
              : String(v)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// One resource row
// ---------------------------------------------------------------------------

const DECISIONS: { value: ReviewDecision; label: string }[] = [
  { value: 'keep_existing', label: 'Keep existing' },
  { value: 'take_import', label: 'Take import' },
  // The match is wrong — these are different records; import as a new one
  // instead of writing onto the matched record.
  { value: 'import_as_new', label: 'Import as new' },
  { value: 'skip', label: 'Skip' },
  { value: 'escalate', label: 'Send to admin' },
];

function ResourceRow({
  row,
  expanded,
  onToggle,
  decision,
  onDecision,
  matchResolution,
  onMatchResolution,
  onCommitPatient,
  commitInFlight,
  onViewDocument,
}: {
  row: ReviewResourceRow;
  expanded: boolean;
  onToggle: () => void;
  decision: ReviewDecision | null;
  onDecision: (d: ReviewDecision | null) => void;
  matchResolution: ReviewMatchResolution | null;
  onMatchResolution: (r: ReviewMatchResolution | null) => void;
  /** Present on decided/decision-free patient rows when the host commits. */
  onCommitPatient?: () => void;
  commitInFlight?: boolean;
  onViewDocument?: () => void;
}) {
  const meta = VERDICT_META[row.verdict];
  const conflict = isConflict(row);
  const ambiguous = isAmbiguous(row);
  const resolved =
    (conflict && decision != null) || (ambiguous && matchResolution != null);

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: (t) =>
          (conflict || ambiguous) && !resolved
            ? alpha(t.palette.warning.main, 0.04)
            : 'transparent',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: ROW_GAP,
          px: 1,
          py: 1,
          cursor: 'pointer',
        }}
      >
        <Chip
          label={row.resourceType}
          size="small"
          variant="outlined"
          sx={{ minWidth: 96, textTransform: 'capitalize' }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {row.resourceType === 'appointment' && row.patientLabel ? (
            // Appointments lead with the patient (whose chart it lands on is the
            // identity); the appointment's own type/date/duration is the detail
            // line. The raw source key is dropped from the row (GDN-2269).
            <>
              {row.patientUnlinked ? (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'warning.main',
                  }}
                >
                  <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />
                  {row.patientLabel} — unlinked
                </Typography>
              ) : (
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {row.patientLabel}
                </Typography>
              )}
              {row.sublabel && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {row.sublabel}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                {row.label}
              </Typography>
              {row.sublabel && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {row.sublabel}
                </Typography>
              )}
              {row.patientLabel &&
                (row.patientUnlinked ? (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'warning.main',
                    }}
                  >
                    <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
                    Unlinked — {row.patientLabel}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: 'text.secondary' }}
                  >
                    Patient: {row.patientLabel}
                  </Typography>
                ))}
            </>
          )}
        </Box>

        <Chip
          icon={resolved ? <CheckCircleOutlinedIcon fontSize="small" /> : meta.icon}
          label={resolved ? 'Resolved' : meta.label}
          size="small"
          color={resolved ? 'success' : meta.color}
          variant="outlined"
        />

        {onViewDocument && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onViewDocument();
            }}
          >
            View document
          </Button>
        )}

        {onCommitPatient && (
          <Tooltip title="Commit this patient record now — children commit later by type">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={commitInFlight}
                onClick={(e) => {
                  e.stopPropagation();
                  onCommitPatient();
                }}
              >
                Commit
              </Button>
            </span>
          </Tooltip>
        )}

        <IconButton size="small" aria-label={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 6, pb: 2, pt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {(ambiguous && row.matchBasis && MATCH_BASIS_BLURB[row.matchBasis]) ||
              VERDICT_BLURB[row.verdict]}
          </Typography>

          {row.matchedSummary && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              Matched existing record: <strong>{row.matchedSummary}</strong>
            </Typography>
          )}

          {conflict && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="warning.main"
                sx={{ fontWeight: 700, display: 'block' }}
              >
                Conflicting values — choose which to keep
              </Typography>
              {row.competing && row.competing.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    mt: 0.5,
                    mb: 1,
                  }}
                >
                  {row.competing.map((d) => (
                    <FieldDiffRow key={d.field} diff={d} mode="competing" />
                  ))}
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, mb: 1 }}
                >
                  Field detail is unavailable for this conflict — decide from
                  the converted data below, or send it to the practice admin.
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DECISIONS.map((d) => (
                  <Button
                    key={d.value}
                    size="small"
                    variant={decision === d.value ? 'contained' : 'outlined'}
                    color={d.value === 'escalate' ? 'warning' : 'primary'}
                    onClick={() =>
                      onDecision(decision === d.value ? null : d.value)
                    }
                  >
                    {d.label}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          {/* Not gated on candidates being present: "create new" must stay
              reachable even if a row somehow arrives with an empty set. */}
          {ambiguous && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="warning.main"
                sx={{ fontWeight: 700, display: 'block' }}
              >
                Which existing patient is this?
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                {(row.matchCandidates ?? []).map((c) => {
                  const selected =
                    matchResolution?.action === 'link' &&
                    matchResolution.targetPatientId === c.patientId;
                  return (
                    <Button
                      key={c.patientId}
                      size="small"
                      variant={selected ? 'contained' : 'outlined'}
                      onClick={() =>
                        onMatchResolution(
                          selected
                            ? null
                            : { action: 'link', targetPatientId: c.patientId },
                        )
                      }
                    >
                      {c.label} · {c.confidence}%
                      {c.identifier ? ` · MRN ${c.identifier}` : ''}
                      {c.reason === 'identifier_only' ? ' (MRN only)' : ''}
                    </Button>
                  );
                })}
                <Button
                  size="small"
                  color="warning"
                  variant={
                    matchResolution?.action === 'create'
                      ? 'contained'
                      : 'outlined'
                  }
                  onClick={() =>
                    onMatchResolution(
                      matchResolution?.action === 'create'
                        ? null
                        : { action: 'create' },
                    )
                  }
                >
                  None of these — create new patient
                </Button>
              </Box>
            </Box>
          )}

          {row.autoMerge && row.autoMerge.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {conflict
                  ? 'Also filling blank fields (automatic)'
                  : 'Filling blank fields — applied automatically, nothing overwritten'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                {row.autoMerge.map((d) => (
                  <FieldDiffRow key={d.field} diff={d} mode="fill" />
                ))}
              </Box>
            </Box>
          )}

          {row.data && (
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Converted data
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <InspectData data={row.data} />
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Browser
// ---------------------------------------------------------------------------

export function ImportReviewBrowser({
  sourceLabel,
  totalRecords,
  totals,
  rows,
  decisions: decisionsProp,
  onDecisionChange,
  matchResolutions: matchResolutionsProp,
  onMatchResolutionChange,
  commit,
  onViewDocument,
  serverBrowse,
  onCommit,
  showCommitBar = true,
  provenance,
}: ImportReviewBrowserProps) {
  const [internalDecisions, setInternalDecisions] = useState<
    Record<string, ReviewDecision>
  >({});
  const decisions = decisionsProp ?? internalDecisions;
  const [internalMatchResolutions, setInternalMatchResolutions] = useState<
    Record<string, ReviewMatchResolution>
  >({});
  const matchResolutions = matchResolutionsProp ?? internalMatchResolutions;

  const [localTypeFilter, setLocalTypeFilter] = useState<string>('all');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all');
  const [localSearch, setLocalSearch] = useState('');
  const typeFilter = serverBrowse ? serverBrowse.typeFilter : localTypeFilter;
  const setTypeFilter = serverBrowse
    ? serverBrowse.onTypeFilterChange
    : setLocalTypeFilter;
  const search = serverBrowse ? serverBrowse.search : localSearch;
  const setSearch = serverBrowse ? serverBrowse.onSearchChange : setLocalSearch;
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Rows needing a decision start expanded — conflicts and ambiguous matches
  // shouldn't be hidden. rows load async (empty on the first render), so a
  // lazy initializer would miss them; auto-expand once, when rows first
  // arrive. Only for small sets: auto-expanding a 500-decision import renders
  // a wall of open diffs (and a very heavy DOM) that buries everything else —
  // past the cap, rows open on click like any list.
  const autoExpandedRef = useRef(false);
  useEffect(() => {
    if (autoExpandedRef.current || rows.length === 0) return;
    autoExpandedRef.current = true;
    const needsDecision = rows.filter((r) => isConflict(r) || isAmbiguous(r));
    if (needsDecision.length > AUTO_EXPAND_MAX) return;
    setExpanded(new Set(needsDecision.map((r) => r.id)));
  }, [rows]);
  const [provenanceOpen, setProvenanceOpen] = useState(false);

  const rowResourceTypes = useMemo(
    () => Array.from(new Set(rows.map((r) => r.resourceType))).sort(),
    [rows],
  );
  const resourceTypes = serverBrowse?.resourceTypes ?? rowResourceTypes;
  const openConflicts = rows.filter(
    (r) => isConflict(r) && decisions[r.id] == null,
  ).length;
  const openMatches = rows.filter(
    (r) => isAmbiguous(r) && matchResolutions[r.id] == null,
  ).length;

  const setDecision = (id: string, d: ReviewDecision | null) => {
    if (!decisionsProp) {
      setInternalDecisions((prev) => {
        if (d == null) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: d };
      });
    }
    onDecisionChange?.(id, d);
  };

  const setMatchResolution = (id: string, r: ReviewMatchResolution | null) => {
    if (!matchResolutionsProp) {
      setInternalMatchResolutions((prev) => {
        if (r == null) {
          const { [id]: _removed, ...rest } = prev;
          return rest;
        }
        return { ...prev, [id]: r };
      });
    }
    onMatchResolutionChange?.(id, r);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      // In server mode the server already applied type + search to its page —
      // re-applying the search to those rows would drop ones the server
      // matched by source key alone. The host's pinned out-of-page rows never
      // went through the server, so the filters apply to them client-side;
      // hiding them is safe because commit gating counts undecided work from
      // the full feeds, not from what's visible (GDN-2247).
      if (!serverBrowse || r.pinned) {
        if (typeFilter !== 'all' && r.resourceType !== typeFilter) return false;
        if (
          q &&
          !`${r.label} ${r.sublabel ?? ''} ${r.patientLabel ?? ''}`
            .toLowerCase()
            .includes(q)
        )
          return false;
      }
      if (verdictFilter !== 'all' && r.verdict !== verdictFilter) return false;
      return true;
    });
    // Needs-attention first (open conflicts / unmatched patients), then by
    // verdict.
    const needsAttention = (r: ReviewResourceRow) =>
      (isConflict(r) && decisions[r.id] == null) ||
      (isAmbiguous(r) && matchResolutions[r.id] == null);
    return filtered.sort((a, b) => {
      const aAttn = needsAttention(a);
      const bAttn = needsAttention(b);
      if (aAttn !== bAttn) return aAttn ? -1 : 1;
      return VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict);
    });
  }, [
    rows,
    typeFilter,
    verdictFilter,
    search,
    decisions,
    matchResolutions,
    serverBrowse,
  ]);

  // Bulk actions target what the operator is looking at — the filtered set —
  // so "keep existing for N conflicts" always matches the rows on screen.
  const visibleOpenConflicts = visible.filter(
    (r) => isConflict(r) && decisions[r.id] == null,
  );
  const visibleOpenMatches = visible.filter(
    (r) => isAmbiguous(r) && matchResolutions[r.id] == null,
  );

  // A row can commit individually once nothing about it awaits a decision.
  const rowCommittable = (r: ReviewResourceRow) =>
    (!isConflict(r) || decisions[r.id] != null) &&
    (!isAmbiguous(r) || matchResolutions[r.id] != null) &&
    r.verdict !== 'unprocessable';
  // Cohort commit: the current verdict filter, as complete patient keys from
  // the host (never just the loaded window). Only patient cohorts commit this
  // way — with a non-patient type filter active the button would lie.
  const cohortKeys =
    commit && verdictFilter !== 'all' && (typeFilter === 'all' || typeFilter === 'patient')
      ? commit.patientKeysByVerdict[verdictFilter]
      : undefined;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openItems = openConflicts + openMatches;
  const commitLabel = (() => {
    const parts: string[] = [];
    if (totals.update)
      parts.push(`${totals.update} update${totals.update > 1 ? 's' : ''}`);
    if (totals.new) parts.push(`${totals.new} new`);
    if (totals.unchanged) parts.push(`${totals.unchanged} unchanged skipped`);
    const head = parts.length ? `Commit — ${parts.join(', ')}` : 'Commit';
    const open: string[] = [];
    if (openConflicts)
      open.push(`${openConflicts} conflict${openConflicts > 1 ? 's' : ''}`);
    if (openMatches)
      open.push(`${openMatches} match${openMatches > 1 ? 'es' : ''}`);
    return open.length ? `${head} · ${open.join(' + ')} open` : head;
  })();

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box>
          <Typography variant="h6">Import review</Typography>
          <Typography variant="body2" color="text.secondary">
            {sourceLabel ? `${sourceLabel} · ` : ''}
            {totalRecords} resources ·{' '}
            {totals.unprocessable
              ? `${totals.unprocessable} couldn't be read`
              : 'all converted'}
          </Typography>
        </Box>
        {provenance && (
          <Button
            size="small"
            onClick={() => setProvenanceOpen((o) => !o)}
            endIcon={
              provenanceOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )
            }
          >
            Provenance
          </Button>
        )}
      </Box>
      {provenance && (
        <Collapse in={provenanceOpen} unmountOnExit>
          <Box sx={{ mb: 2 }}>{provenance}</Box>
        </Collapse>
      )}

      {/* Summary + verdict filter chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Chip
          label={`All ${totalRecords}`}
          size="small"
          variant={verdictFilter === 'all' ? 'filled' : 'outlined'}
          onClick={() => setVerdictFilter('all')}
        />
        {VERDICT_ORDER.filter((v) => totals[v] > 0).map((v) => (
          <Chip
            key={v}
            icon={VERDICT_META[v].icon}
            label={`${VERDICT_META[v].label} ${totals[v]}`}
            size="small"
            color={VERDICT_META[v].color}
            variant={verdictFilter === v ? 'filled' : 'outlined'}
            onClick={() => setVerdictFilter(v)}
          />
        ))}
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          select
          size="small"
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All types</MenuItem>
          {resourceTypes.map((t) => (
            <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
              {t}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="Search records…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {/* Bulk actions: one decision applied to every still-open VISIBLE item
          (the current filters scope them). Each is individually reversible
          afterwards, so no confirm dialog. */}
      {(visibleOpenConflicts.length > 0 || visibleOpenMatches.length > 0) && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Bulk:
          </Typography>
          {visibleOpenConflicts.length > 0 && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  visibleOpenConflicts.forEach((r) =>
                    setDecision(r.id, 'keep_existing'),
                  )
                }
              >
                Keep existing for {visibleOpenConflicts.length} conflict
                {visibleOpenConflicts.length > 1 ? 's' : ''}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  visibleOpenConflicts.forEach((r) =>
                    setDecision(r.id, 'take_import'),
                  )
                }
              >
                Take import for {visibleOpenConflicts.length} conflict
                {visibleOpenConflicts.length > 1 ? 's' : ''}
              </Button>
            </>
          )}
          {visibleOpenMatches.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() =>
                visibleOpenMatches.forEach((r) =>
                  setMatchResolution(r.id, { action: 'create' }),
                )
              }
            >
              Create new patient for {visibleOpenMatches.length} unmatched
            </Button>
          )}
        </Box>
      )}

      {/* Cohort commit (GDN-2251): commit every patient with the filtered
          verdict — the complete set from the host, not just this page. The
          server re-checks decisions, so a premature click blocks cleanly. */}
      {commit && cohortKeys && cohortKeys.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Tooltip title="Commits these patient records now — their records (encounters, meds, …) commit later by type">
            <span>
              <Button
                size="small"
                variant="contained"
                disabled={commit.inFlight}
                onClick={() => commit.onCommitPatients(cohortKeys)}
              >
                Commit {cohortKeys.length.toLocaleString()}{' '}
                {VERDICT_META[verdictFilter as ImportVerdict].label.toLowerCase()}{' '}
                patient{cohortKeys.length > 1 ? 's' : ''}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {/* List */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
        {visible.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center' }} color="text.secondary">
            No resources match this filter.
          </Typography>
        ) : (
          visible.map((row) => (
            <ResourceRow
              key={row.id}
              row={row}
              expanded={expanded.has(row.id)}
              onToggle={() => toggle(row.id)}
              decision={decisions[row.id] ?? null}
              onDecision={(d) => setDecision(row.id, d)}
              matchResolution={matchResolutions[row.id] ?? null}
              onMatchResolution={(r) => setMatchResolution(row.id, r)}
              onCommitPatient={
                commit && row.patientKey && rowCommittable(row)
                  ? () => commit.onCommitPatients([row.patientKey as string])
                  : undefined
              }
              commitInFlight={commit?.inFlight}
              onViewDocument={
                onViewDocument && row.resourceType === 'document_reference' && !row.pinned
                  ? () => onViewDocument(row.id)
                  : undefined
              }
            />
          ))
        )}
      </Box>

      {/* Server-driven pagination: filters + paging cover the whole import. */}
      {serverBrowse && serverBrowse.totalRows > serverBrowse.perPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
          <Pagination
            page={serverBrowse.page}
            count={Math.max(
              1,
              Math.ceil(serverBrowse.totalRows / serverBrowse.perPage),
            )}
            onChange={(_, page) => serverBrowse.onPageChange(page)}
            size="small"
          />
        </Box>
      )}

      {/* Commit bar (host may own commit instead) */}
      {showCommitBar && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {openItems > 0
              ? `${openItems} item${openItems > 1 ? 's' : ''} still need a decision before commit.`
              : 'All conflicts and matches resolved — ready to commit.'}
          </Typography>
          <Button
            variant="contained"
            color={openItems > 0 ? 'warning' : 'primary'}
            disabled={openItems > 0}
            onClick={onCommit}
          >
            {commitLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
